import { useEffect, useMemo, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Search, Pencil, Archive, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/common";
import { motion } from "framer-motion";

const PRIORITY_LABELS = { basse: "Basse", normale: "Normale", haute: "Haute", urgente: "Urgente" };

export default function CategoriesPage() {
  const { user } = useAuth();
  const [categories, setCategories] = useState(null);
  const [staff, setStaff] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const isSuper = user?.role === "super_admin";
  const [form, setForm] = useState({ name: "", description: "", color: "#DC2626", default_priority: "normale",
    visible_to_client: true, assignee_id: "", manager_id: "", secondary_ids: [], company_id: "" });
  const [search, setSearch] = useState("");
  const [fCompany, setFCompany] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fVisible, setFVisible] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ sector: "", name: "", description: "", color: "#DC2626", default_priority: "normale" });
  const [tplDelete, setTplDelete] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);

  const loadTemplates = () => {
    if (isSuper) api.get("/category-templates").then(({ data }) => setTemplates(data)).catch(() => {});
  };

  const groupedTemplates = useMemo(() => {
    const g = {};
    templates.forEach((t) => { (g[t.sector] = g[t.sector] || []).push(t); });
    return g;
  }, [templates]);

  const saveTemplate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/category-templates", { ...tplForm, description: tplForm.description || null });
      toast.success("Modèle ajouté");
      setTplOpen(false);
      setTplForm({ sector: "", name: "", description: "", color: "#DC2626", default_priority: "normale" });
      loadTemplates();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const doDeleteTemplate = async () => {
    try {
      await api.delete(`/category-templates/${tplDelete.id}`);
      toast.success("Modèle supprimé");
      loadTemplates();
    } catch (e) { toast.error(fmtErr(e)); throw e; }
  };

  const visible = useMemo(() => {
    let list = categories || [];
    if (search) list = list.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
    if (fCompany) list = list.filter((c) => c.company_id === fCompany);
    if (fStatus) list = list.filter((c) => c.status === fStatus);
    if (fVisible === "oui") list = list.filter((c) => c.visible_to_client);
    if (fVisible === "non") list = list.filter((c) => !c.visible_to_client);
    return list;
  }, [categories, search, fCompany, fStatus, fVisible]);

  const load = () => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch((e) => toast.error(fmtErr(e)));
    if (["super_admin", "company_admin"].includes(user?.role)) {
      api.get("/users").then(({ data }) => setStaff(data.filter((u) => u.role !== "super_admin"))).catch(() => {});
    }
    if (isSuper) api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {});
    loadTemplates();
  };
  useEffect(() => { load(); }, []);

  const companyStaff = isSuper && form.company_id ? staff.filter((u) => u.company_id === form.company_id) : staff;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/categories", {
        name: form.name, description: form.description || null, color: form.color,
        default_priority: form.default_priority, visible_to_client: form.visible_to_client,
        assignee_id: form.assignee_id || null, manager_id: form.manager_id || null,
        secondary_ids: form.secondary_ids,
        company_id: isSuper ? form.company_id : null,
      });
      setOpen(false);
      setForm({ name: "", description: "", color: "#DC2626", default_priority: "normale", visible_to_client: true, assignee_id: "", manager_id: "", secondary_ids: [], company_id: "" });
      load();
      toast.success("Catégorie créée");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (c) => {
    try {
      await api.patch(`/categories/${c.id}`, { status: c.status === "active" ? "inactive" : "active" });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const openEdit = (c) => {
    setEditTarget({
      id: c.id, name: c.name, description: c.description || "", color: c.color || "#DC2626",
      default_priority: c.default_priority || "normale", visible_to_client: !!c.visible_to_client,
      assignee_id: c.assignee_id || "", manager_id: c.manager_id || "", secondary_ids: c.secondary_ids || [], company_id: c.company_id,
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/categories/${editTarget.id}`, {
        name: editTarget.name, description: editTarget.description || null, color: editTarget.color,
        default_priority: editTarget.default_priority, visible_to_client: editTarget.visible_to_client,
        assignee_id: editTarget.assignee_id || null, manager_id: editTarget.manager_id || null,
        secondary_ids: editTarget.secondary_ids,
      });
      toast.success("Catégorie mise à jour");
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const doArchive = async () => {
    try {
      await api.patch(`/categories/${archiveTarget.id}`, { status: archiveTarget.status === "archived" ? "active" : "archived" });
      toast.success(archiveTarget.status === "archived" ? "Catégorie restaurée" : "Catégorie archivée");
      load();
    } catch (e) { toast.error(fmtErr(e)); throw e; }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/categories/${deleteTarget.id}`);
      toast.success("Catégorie supprimée");
      load();
    } catch (e) { toast.error(fmtErr(e)); throw e; }
  };

  const editStaff = isSuper && editTarget ? staff.filter((u) => u.company_id === editTarget.company_id) : staff;

  return (
    <div data-testid="categories-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Catégories de tickets</h1>
          <p className="mt-1 text-sm text-muted-foreground">Chaque catégorie appartient à une seule société. Auto-assignation au collaborateur principal de la même société.</p>
        </div>
        <Button data-testid="create-category-button" onClick={() => setOpen(true)} className="rounded-sm bg-primary text-white hover:bg-red-700">
          <Plus className="mr-2 h-4 w-4" /> Nouvelle catégorie
        </Button>
      </div>

      <div className="mb-5 flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="search-categories" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une catégorie…" className="w-60 rounded-sm pl-9" />
        </div>
        {isSuper && (
          <Select value={fCompany} onValueChange={(v) => setFCompany(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-category-company" className="w-48 rounded-sm"><SelectValue placeholder="Société : toutes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Société : toutes</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={fStatus} onValueChange={(v) => setFStatus(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-category-status" className="w-44 rounded-sm"><SelectValue placeholder="Statut : tous" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Statut : tous</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="archived">Archivée</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fVisible} onValueChange={(v) => setFVisible(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-category-visibility" className="w-52 rounded-sm"><SelectValue placeholder="Visibilité : toutes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Visibilité : toutes</SelectItem>
            <SelectItem value="oui">Visible client</SelectItem>
            <SelectItem value="non">Non visible client</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isSuper && (
        <div className="mb-6 rounded-sm border border-border bg-card" data-testid="sector-templates-section">
          <button data-testid="toggle-templates-section" onClick={() => setShowTemplates(!showTemplates)}
            className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-accent transition-colors duration-200">
            <div>
              <div className="font-semibold">Modèles de catégories par secteur</div>
              <div className="text-xs text-muted-foreground">Modèles de départ applicables à la création d'une société — les catégories créées restent propres à chaque société.</div>
            </div>
            <span className="text-xs font-semibold text-primary">{showTemplates ? "Masquer" : "Afficher"}</span>
          </button>
          {showTemplates && (
            <div className="border-t border-border p-5">
              <div className="mb-4 flex justify-end">
                <Button size="sm" data-testid="create-template-button" onClick={() => setTplOpen(true)} className="rounded-sm bg-primary text-white hover:bg-red-700">
                  <Plus className="mr-2 h-4 w-4" /> Ajouter un modèle
                </Button>
              </div>
              {Object.entries(groupedTemplates).map(([sector, tpls]) => (
                <div key={sector} className="mb-5 last:mb-0" data-testid={`template-sector-${sector}`}>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{sector}</div>
                  <div className="flex flex-wrap gap-2">
                    {tpls.map((t) => (
                      <span key={t.id} data-testid={`template-${t.id}`}
                        className="inline-flex items-center gap-2 rounded-sm border border-border px-3 py-1.5 text-sm">
                        <span className="h-2 w-2 rounded-full" style={{ background: t.color }} />
                        {t.name}
                        <span className="text-xs text-muted-foreground">({PRIORITY_LABELS[t.default_priority]})</span>
                        <button data-testid={`delete-template-${t.id}`} onClick={() => setTplDelete(t)} className="text-muted-foreground hover:text-primary transition-colors" title="Supprimer">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
              {!templates.length && <div className="text-sm text-muted-foreground">Aucun modèle. Ajoutez-en pour proposer des catégories de départ par secteur.</div>}
            </div>
          )}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {visible.map((c, i) => (
          <motion.div key={c.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            className="rounded-sm border border-border bg-card p-5" data-testid={`category-card-${c.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: c.color }} />
                <div className="min-w-0">
                  <div className="truncate font-semibold">{c.name}</div>
                  <div className="text-xs text-muted-foreground">Priorité par défaut : {PRIORITY_LABELS[c.default_priority]}</div>
                  {isSuper && companies.length > 0 && (
                    <div className="mt-0.5 text-xs font-medium text-primary">{companies.find((co) => co.id === c.company_id)?.name || ""}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`edit-category-${c.id}`} onClick={() => openEdit(c)} title="Modifier"
                  className="rounded-sm border border-border p-1.5 text-muted-foreground hover:text-foreground transition-colors">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button data-testid={`toggle-category-${c.id}`} onClick={() => toggleStatus(c)} title={c.status === "active" ? "Désactiver" : "Activer"}
                  className={`rounded-sm px-2 py-0.5 text-xs font-semibold transition-colors ${c.status === "active" ? "bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-300" : c.status === "archived" ? "bg-neutral-200 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400" : "bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400"}`}>
                  {c.status === "active" ? "Active" : c.status === "archived" ? "Archivée" : "Inactive"}
                </button>
                <button data-testid={`archive-category-${c.id}`} onClick={() => setArchiveTarget(c)} title={c.status === "archived" ? "Restaurer" : "Archiver"}
                  className="rounded-sm border border-border p-1.5 text-muted-foreground hover:text-primary transition-colors">
                  <Archive className="h-3.5 w-3.5" />
                </button>
                {isSuper && (
                  <button data-testid={`delete-category-${c.id}`} onClick={() => setDeleteTarget(c)} title="Supprimer"
                    className="rounded-sm border border-border p-1.5 text-muted-foreground hover:text-primary transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
            {c.description && <p className="mt-3 text-sm text-muted-foreground">{c.description}</p>}
            <div className="mt-4 space-y-1 text-xs text-muted-foreground">
              <div>Collaborateur principal : <span className="font-medium text-foreground">{c.assignee_name || "—"}</span></div>
              <div>Manager responsable : <span className="font-medium text-foreground">{c.manager_name || "—"}</span></div>
              {(c.secondary_names || []).length > 0 && (
                <div>Collaborateurs secondaires : <span className="font-medium text-foreground">{c.secondary_names.join(", ")}</span></div>
              )}
              <div>Visible client : <span className="font-medium text-foreground">{c.visible_to_client ? "Oui" : "Non"}</span></div>
              {(c.sla_first_response_hours || c.sla_resolution_hours) && (
                <div>SLA : {c.sla_first_response_hours ? `1re réponse ${c.sla_first_response_hours}h` : ""}{c.sla_first_response_hours && c.sla_resolution_hours ? " · " : ""}{c.sla_resolution_hours ? `résolution ${c.sla_resolution_hours}h` : ""}</div>
              )}
            </div>
          </motion.div>
        ))}
      </div>
      {categories && visible.length === 0 && <div className="rounded-sm border border-dashed border-border p-10 text-center text-sm text-muted-foreground">Aucune catégorie avec ces filtres.</div>}

      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="edit-category-dialog">
          <DialogHeader>
            <DialogTitle>Modifier la catégorie</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input data-testid="edit-category-name" required value={editTarget.name} onChange={(e) => setEditTarget({ ...editTarget, name: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea data-testid="edit-category-description" value={editTarget.description} onChange={(e) => setEditTarget({ ...editTarget, description: e.target.value })} className="rounded-sm" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Couleur</Label>
                  <div className="flex items-center gap-2">
                    <input data-testid="edit-category-color" type="color" value={editTarget.color} onChange={(e) => setEditTarget({ ...editTarget, color: e.target.value })} className="h-9 w-12 cursor-pointer rounded-sm border border-border bg-transparent" />
                    <code className="text-xs text-muted-foreground">{editTarget.color}</code>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Priorité par défaut</Label>
                  <Select value={editTarget.default_priority} onValueChange={(v) => setEditTarget({ ...editTarget, default_priority: v })}>
                    <SelectTrigger data-testid="edit-category-priority" className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Collaborateur principal</Label>
                  <Select value={editTarget.assignee_id || "none"} onValueChange={(v) => setEditTarget({ ...editTarget, assignee_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="edit-category-assignee" className="rounded-sm"><SelectValue placeholder="Aucun" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {editStaff.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Manager responsable</Label>
                  <Select value={editTarget.manager_id || "none"} onValueChange={(v) => setEditTarget({ ...editTarget, manager_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="edit-category-manager" className="rounded-sm"><SelectValue placeholder="Aucun" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {editStaff.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Collaborateurs secondaires</Label>
                <div className="grid grid-cols-2 gap-2">
                  {editStaff.map((u) => (
                    <label key={u.id} data-testid={`edit-category-secondary-${u.id}`} className="flex cursor-pointer items-center gap-2 rounded-sm border border-border p-2.5 text-sm hover:bg-accent transition-colors">
                      <Checkbox checked={(editTarget.secondary_ids || []).includes(u.id)}
                        onCheckedChange={(v) => setEditTarget({ ...editTarget, secondary_ids: v ? [...(editTarget.secondary_ids || []), u.id] : (editTarget.secondary_ids || []).filter((x) => x !== u.id) })} />
                      <span className="truncate">{u.first_name} {u.last_name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between rounded-sm border border-border p-3">
                <Label>Visible par les clients (portail)</Label>
                <Switch data-testid="edit-category-visible" checked={editTarget.visible_to_client} onCheckedChange={(v) => setEditTarget({ ...editTarget, visible_to_client: v })} />
              </div>
              <DialogFooter>
                <Button data-testid="edit-category-submit" type="submit" disabled={saving} className="rounded-sm bg-primary text-white hover:bg-red-700">
                  {saving ? "Enregistrement…" : "Enregistrer"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!archiveTarget} onOpenChange={() => setArchiveTarget(null)}
        title={archiveTarget?.status === "archived" ? `Restaurer « ${archiveTarget?.name} » ?` : `Archiver « ${archiveTarget?.name} » ?`}
        description={archiveTarget?.status === "archived"
          ? "La catégorie redeviendra active et visible selon ses paramètres."
          : "La catégorie ne sera plus proposée à la création de tickets. Les tickets existants sont conservés. Action journalisée."}
        confirmLabel={archiveTarget?.status === "archived" ? "Restaurer" : "Archiver"}
        onConfirm={doArchive} testid="archive-category-dialog" />

      <ConfirmDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}
        title={`Supprimer définitivement « ${deleteTarget?.name} » ?`}
        description="Action irréversible réservée au Super Admin. Impossible si des tickets utilisent cette catégorie — dans ce cas, archivez-la. Action journalisée."
        confirmLabel="Supprimer définitivement" onConfirm={doDelete} testid="delete-category-dialog" />

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="rounded-sm" data-testid="create-template-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau modèle de catégorie</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveTemplate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Secteur d'activité *</Label>
                <Input data-testid="template-sector-input" required list="sector-list" value={tplForm.sector}
                  onChange={(e) => setTplForm({ ...tplForm, sector: e.target.value })} className="rounded-sm" placeholder="Ex : Nettoyage" />
                <datalist id="sector-list">
                  {Object.keys(groupedTemplates).map((s) => <option key={s} value={s} />)}
                </datalist>
              </div>
              <div className="space-y-2">
                <Label>Nom de la catégorie *</Label>
                <Input data-testid="template-name-input" required value={tplForm.name}
                  onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} className="rounded-sm" placeholder="Ex : Réclamation nettoyage" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input data-testid="template-description-input" value={tplForm.description}
                onChange={(e) => setTplForm({ ...tplForm, description: e.target.value })} className="rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Couleur</Label>
                <div className="flex items-center gap-2">
                  <input data-testid="template-color-input" type="color" value={tplForm.color}
                    onChange={(e) => setTplForm({ ...tplForm, color: e.target.value })} className="h-9 w-12 cursor-pointer rounded-sm border border-border bg-transparent" />
                  <code className="text-xs text-muted-foreground">{tplForm.color}</code>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Priorité par défaut</Label>
                <Select value={tplForm.default_priority} onValueChange={(v) => setTplForm({ ...tplForm, default_priority: v })}>
                  <SelectTrigger data-testid="template-priority-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="template-submit-button" type="submit" disabled={saving} className="rounded-sm bg-primary text-white hover:bg-red-700">
                {saving ? "Ajout…" : "Ajouter le modèle"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!tplDelete} onOpenChange={() => setTplDelete(null)}
        title={`Supprimer le modèle « ${tplDelete?.name} » ?`}
        description="Le modèle sera retiré du secteur. Les catégories déjà créées dans les sociétés ne sont pas affectées. Action journalisée."
        confirmLabel="Supprimer" onConfirm={doDeleteTemplate} testid="delete-template-dialog" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="create-category-dialog">
          <DialogHeader>
            <DialogTitle>Nouvelle catégorie</DialogTitle>
            <DialogDescription>Les tickets créés avec cette catégorie seront auto-assignés au collaborateur principal.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input data-testid="category-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" placeholder="Ex : Réclamation nettoyage" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea data-testid="category-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Couleur</Label>
                <div className="flex items-center gap-2">
                  <input data-testid="category-color-input" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 w-12 cursor-pointer rounded-sm border border-border bg-transparent" />
                  <code className="text-xs text-muted-foreground">{form.color}</code>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Priorité par défaut</Label>
                <Select value={form.default_priority} onValueChange={(v) => setForm({ ...form, default_priority: v })}>
                  <SelectTrigger data-testid="category-priority-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {isSuper && (
              <div className="space-y-2">
                <Label>Société *</Label>
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v, assignee_id: "", manager_id: "" })}>
                  <SelectTrigger data-testid="category-company-select" className="rounded-sm"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Collaborateur principal</Label>
                <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                  <SelectTrigger data-testid="category-assignee-select" className="rounded-sm"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    {companyStaff.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Manager responsable</Label>
                <Select value={form.manager_id} onValueChange={(v) => setForm({ ...form, manager_id: v })}>
                  <SelectTrigger data-testid="category-manager-select" className="rounded-sm"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    {companyStaff.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Collaborateurs secondaires</Label>
              <div className="grid grid-cols-2 gap-2">
                {companyStaff.map((u) => (
                  <label key={u.id} data-testid={`category-secondary-${u.id}`} className="flex cursor-pointer items-center gap-2 rounded-sm border border-border p-2.5 text-sm hover:bg-accent transition-colors">
                    <Checkbox checked={form.secondary_ids.includes(u.id)}
                      onCheckedChange={(v) => setForm({ ...form, secondary_ids: v ? [...form.secondary_ids, u.id] : form.secondary_ids.filter((x) => x !== u.id) })} />
                    <span className="truncate">{u.first_name} {u.last_name}</span>
                  </label>
                ))}
                {companyStaff.length === 0 && <p className="col-span-full text-xs text-muted-foreground">Sélectionnez d'abord une société.</p>}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-sm border border-border p-3">
              <Label>Visible par les clients (portail)</Label>
              <Switch data-testid="category-visible-switch" checked={form.visible_to_client} onCheckedChange={(v) => setForm({ ...form, visible_to_client: v })} />
            </div>
            <DialogFooter>
              <Button data-testid="category-submit-button" type="submit" disabled={saving || (isSuper && !form.company_id)} className="rounded-sm bg-primary text-white hover:bg-red-700">
                {saving ? "Création…" : "Créer la catégorie"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
